'use client'

import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { cn } from '@tebuto/lib/utils'
import { Form, Formik, FormikHelpers, FormikValues, useFormikContext } from 'formik'
import { ReactNode } from 'react'
import FormButtons from '../forms/Buttons'
import { useSelectFirstInput } from './hooks'
import PlainModal from './PlainModal'

export interface ITebutoModal<T extends FormikValues> {
    isOpen: boolean
    title: string
    subTitle?: string
    initialValues?: T
    description?: string
    validationSchema?: any
    validateOnChange?: boolean
    validateOnBlur?: boolean
    validateOnMount?: boolean
    enableReinitialize?: boolean
    onSubmit?: (values: T, formikHelpers: FormikHelpers<T>) => void
    cancelButtonText?: string
    cancelButtonEnabled?: boolean
    confirmButtonEnabled?: boolean
    confirmButtonText?: string
    showResetButton?: boolean
    verticalPadding?: boolean
    className?: string
    children: ReactNode | ReactNode[]
    closeHandler: () => void
    onModalClose?: () => void
    customButtonBar?: ReactNode
    submitButtonTestId?: string
    cancelButtonTestId?: string
    'data-testid'?: string
}

/**
 * @deprecated Compose instead:
 *   <Modal isOpen onClose={close} title="…">
 *     <Formik …><Form><Modal.Body>…</Modal.Body><Modal.Footer><FormActions/></Modal.Footer></Form></Formik>
 *   </Modal>
 * Kept working during the migration window.
 */
export default function TebutoModal<T extends FormikValues = FormikValues>({
    initialValues = {} as T,
    onSubmit = () => {},
    validationSchema,
    cancelButtonText,
    confirmButtonText,
    confirmButtonEnabled,
    cancelButtonEnabled = true,
    validateOnBlur = true,
    validateOnChange = true,
    validateOnMount = false,
    enableReinitialize = false,
    className = '',
    children,
    closeHandler,
    onModalClose = closeHandler,
    showResetButton = true,
    verticalPadding = true,
    customButtonBar,
    submitButtonTestId,
    cancelButtonTestId,
    ...props
}: ITebutoModal<T>) {
    const { t } = useComponentTranslation()

    cancelButtonText = cancelButtonText || t('cancel', { context: 'action' })

    return (
        <PlainModal
            {...props}
            className={className}
            verticalPadding={false}
            closeHandler={closeHandler}
            onModalClose={onModalClose}
        >
            <Formik<T>
                validateOnChange={validateOnChange}
                validateOnBlur={validateOnBlur}
                validateOnMount={validateOnMount}
                enableReinitialize={enableReinitialize}
                initialValues={initialValues}
                onSubmit={onSubmit}
                onReset={closeHandler}
                validationSchema={validationSchema}
            >
                {({ isValid }) => (
                    <FormContent<T>
                        confirmButtonText={confirmButtonText}
                        confirmButtonEnabled={confirmButtonEnabled !== undefined ? confirmButtonEnabled : isValid}
                        cancelButtonEnabled={cancelButtonEnabled}
                        cancelButtonText={cancelButtonText}
                        showResetButton={showResetButton}
                        verticalPadding={verticalPadding}
                        closeHandler={closeHandler}
                        customButtonBar={customButtonBar}
                        submitButtonTestId={submitButtonTestId}
                        cancelButtonTestId={cancelButtonTestId}
                    >
                        {children}
                    </FormContent>
                )}
            </Formik>
        </PlainModal>
    )
}

function FormContent<T extends FormikValues>({
    children,
    confirmButtonText,
    confirmButtonEnabled,
    cancelButtonEnabled,
    cancelButtonText,
    showResetButton,
    verticalPadding,
    customButtonBar,
    submitButtonTestId,
    cancelButtonTestId
}: Partial<ITebutoModal<T>>) {
    const { isSubmitting: loading, isValid, dirty } = useFormikContext<T>()
    const { modalBodyRef } = useSelectFirstInput()
    return (
        <Form
            className={cn('flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-x-hidden overflow-y-hidden')}
        >
            <div
                className={cn(
                    'min-h-0 min-w-0 max-w-full grow overflow-x-hidden overflow-y-auto px-6 md:px-10',
                    verticalPadding ? 'py-8 md:py-12' : ''
                )}
                ref={modalBodyRef}
            >
                {children}
            </div>
            <div
                className={cn('min-w-0 max-w-full shrink-0 border-t border-gray-200 px-6 py-4 md:px-10 bg-primary-25')}
            >
                {!!customButtonBar && customButtonBar}
                {!customButtonBar && (
                    <FormButtons
                        saveButtonLabel={confirmButtonText}
                        resetButtonLabel={cancelButtonText}
                        loading={loading}
                        showResetButton={showResetButton}
                        saveButtonEnabled={confirmButtonEnabled === undefined ? dirty && isValid : confirmButtonEnabled}
                        resetButtonEnabled={cancelButtonEnabled}
                        submitButtonTestId={submitButtonTestId}
                        resetButtonTestId={cancelButtonTestId}
                    />
                )}
            </div>
        </Form>
    )
}
